import { useState } from 'react';
import { SatisfactionSurveySettingsSectionProps } from '../satisfactionSurveySettings.types';
import { isValidSurveyUrl } from '../satisfactionSurveySettings.utils';
import useSyncedFieldValue from './useSyncedFieldValue';

type UseSatisfactionSurveyContentFieldsParams = Omit<
  SatisfactionSurveySettingsSectionProps,
  'isDisabled'
>;

const useSatisfactionSurveyContentFields = ({
  settings,
  isSaving,
  rejectedSaves,
  onChange,
}: UseSatisfactionSurveyContentFieldsParams) => {
  const url = useSyncedFieldValue({ savedValue: settings.url ?? '', rejectedSaves, isSaving });
  const message = useSyncedFieldValue({
    savedValue: settings.message ?? '',
    rejectedSaves,
    isSaving,
  });

  const [isUrlFocused, setIsUrlFocused] = useState(false);

  const trimmedUrl = url.value.trim();
  const hasInvalidUrl = !isUrlFocused && !!trimmedUrl && !isValidSurveyUrl(trimmedUrl);

  const onUrlFocus = () => {
    setIsUrlFocused(true);
    url.onFocus();
  };

  const onUrlBlur = () => {
    setIsUrlFocused(false);

    if (url.onBlur() || trimmedUrl === (settings.url ?? '')) {
      return;
    }

    if (!trimmedUrl) {
      onChange({ url: null, enabled: false });
      return;
    }

    if (isValidSurveyUrl(trimmedUrl)) {
      onChange({ url: trimmedUrl });
    }
  };

  const onMessageBlur = () => {
    const hasDiscardedTypedValue = message.onBlur();

    if (hasDiscardedTypedValue) {
      return;
    }

    const nextValue = message.value.trim() || null;

    if (nextValue === settings.message) {
      return;
    }

    onChange({ message: nextValue });
  };

  return { url, message, hasInvalidUrl, onUrlFocus, onUrlBlur, onMessageBlur };
};

export default useSatisfactionSurveyContentFields;
